import { createRef } from 'react';

import { withScope, captureException, captureMessage } from '@sentry/react';
import jwtDecode from 'jwt-decode';

import { setConsentPending } from 'actions/auth';
import {
  loginWithConsent,
  oAuthLoginSuccess,
  signupClicked,
  userSignupFail,
  userSignupSuccess,
} from 'actions/user';
import { postMessageToMobileApp } from 'components/app/helpers';
import { getMixpanelPageName } from 'components/primaryHeader/partials/universalSearch/partials/helpers';
import { OAUTH, USER_PROFILE } from 'constants/api';
import {
  API_URL,
  ERROR_CODES,
  IS_INDIAN_EXCHANGE,
  IS_RN_WEBVIEW_ANDROID,
  isRNWebview,
  RELEASE_VERSION,
  SIGNUP_ERROR_CODES,
} from 'constants/constants';
import {
  OAUTH_PROVIDERS,
  POST_MESSAGE_TO_MOBILE_EVENTS,
  STORAGE_KEYS,
} from 'constants/enums';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { IS_HYBRID_APP_SUPPORTED } from 'constants/rnApp';
import routes from 'constants/routes';
import {
  checkRefCodeValidForSendingAttributionData,
  dispatchGtmSignupEvent,
  getDeviceOSTypeForAnalytics,
  getTrackierClickId,
  removeTrackierClickId,
} from 'helpers/analytics';
import { getDeviceId } from 'helpers/deviceId';
import {
  identifyUser,
  mixpanelStartRecording,
  setUserProperty,
  trackMixpanelEvent,
} from 'helpers/mixpanel-init';
import MoengageLib from 'helpers/moengage';
import { contains, isEmpty as isEmptyRamda, omit } from 'helpers/ramda';
import { addLogsInSentryAndMixpanel, addLogsToSentry } from 'helpers/sentry';
import {
  fetchWithTimeout,
  isDemo,
  isEmpty,
  validateEmail as validateEmailPattern,
} from 'helpers/utils';
import i18n from 'i18n/config';

import { redirectToEmailVerification } from '../Common/helpers';
import loginActions from '../Login/actions';
import { DeletedAccountExistsMessage } from '../Login/partials/Messages';
import { sendTrackierSignupEvent } from '../SignupVerification/helpers';
import actions from './actions';
import { countryNotValid, DEFAULT_REFERRAL_CODE } from './constants';
import {
  AlreadyRegisteredMessage,
  AlreadyRegisteredUnverifiedMessage,
  CountryNotServedMessage,
} from './partials/Messages';

// regex for referral code (alpha numeric with max length 20)
export const referralRegex = /^[a-zA-Z0-9]{0,20}$/;

const ONE_UPPERCASE_LETTER_PATTERN = /[A-Z]/;
const ONE_LOWERCASE_LETTER_PATTERN = /[a-z]/;
const ONE_SPECIAL_CHARACTER_PATTERN = /\W|_/;
const ONE_NUMBER_PATTERN = /\d/;

// Create a filter for your search feature.
// Will filter by "includes" and then sort by "startsWith"
const createSearchFilter = terms => filterTerm => {
  const lowerCaseFilterTerm = filterTerm.toLowerCase();
  return terms
    .filter(v => v.toLowerCase().includes(lowerCaseFilterTerm))
    .sort((a, b) => {
      const aStarts = a.toLowerCase().startsWith(lowerCaseFilterTerm);
      const bStarts = b.toLowerCase().startsWith(lowerCaseFilterTerm);
      if (aStarts && bStarts) return a.localeCompare(b);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return a.localeCompare(b);
    });
};

export const getFilteredCountries = countryList =>
  createSearchFilter(countryList.map(country => country.label));

export const allPwdCriteriaSatisfied = (password = '') => {
  let satisfied = true;

  if (password.length < 6) satisfied = false;
  if (!ONE_UPPERCASE_LETTER_PATTERN.test(password)) satisfied = false;
  if (!ONE_LOWERCASE_LETTER_PATTERN.test(password)) satisfied = false;
  if (!ONE_SPECIAL_CHARACTER_PATTERN.test(password)) satisfied = false;
  if (!ONE_NUMBER_PATTERN.test(password)) satisfied = false;

  return satisfied;
};

/**
 * @param {import('./interface').SignUpState} state
 * @returns {boolean}
 */
export const canSubmit = state => {
  const consentGiven =
    state.nriConsent.indianResident && state.nriConsent.termsAndPrivacy;

  // Conditions to Enable Submit.
  const fieldsNotEmpty = state.country.value && state.email.value && state.password.value;

  const noError =
    !state.country.error &&
    !state.email.error &&
    !state.password.error &&
    !state.country.error;

  const enableSubmit = fieldsNotEmpty && noError && consentGiven;

  const failureConditions = {};

  if (state.email.value === '') {
    failureConditions.empty_email = true;
  } else if (state.email.error) {
    failureConditions.invalid_email = true;
  }

  if (state.password.value === '') {
    failureConditions.empty_password = true;
  } else if (state.password.score < 2) {
    failureConditions.invalid_password = true;
  }

  if (state.country.value === '') {
    failureConditions.empty_country = true;
  }

  if (!isEmptyRamda(failureConditions)) {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.BUTTON_CLICKED_WITH_MISSING_FIELDS, {
      ...failureConditions,
      country: state.country.value,
      user_email: state.email.value,
      referral_code: state.referral.value,
      password_score: state.password.score,
      page_name: 'Sign-up',
      type: 'email',
    });
  }

  if (!consentGiven) {
    let missingConsent = 'both';
    if (state.nriConsent.indianResident && !state.nriConsent.termsAndPrivacy)
      missingConsent = 'terms_&_privacy';
    else if (!state.nriConsent.indianResident && state.nriConsent.termsAndPrivacy)
      missingConsent = 'indian_resident';
    trackMixpanelEvent(
      MIXPANEL_EVENT_NAMES.CONSENT.SIGNUP_BUTTON_CLICKED_CONSENT_MISSING,
      {
        type: 'email',
        consent_type: missingConsent,
      }
    );
  }

  return enableSubmit;
};

export const getPasswordValidation = (password, email, zxcvbn, options) => {
  const validation = {
    error: false,
    errorMessage: '',
    score: zxcvbn(password).score,
    errorCode: '',
  };

  // Validation only on Submit Press.
  if (options?.submit) {
    if (isEmpty(password)) {
      validation.error = true;
      validation.errorMessage = i18n.t('errors:custom.password_empty');
      return validation;
    }
    if (options?.errorCode) {
      switch (options.errorCode) {
        case ERROR_CODES.BCRYPT_HASH_LIMIT:
          validation.error = true;
          validation.errorMessage = i18n.t(
            `errors:custom.${ERROR_CODES.BCRYPT_HASH_LIMIT}`
          );
          break;
        default:
          break;
      }
      return validation;
    }
  }

  // Live Validation.
  if (isEmpty(password)) {
    return validation;
  }

  let passwordIsAllowed = false;
  if (password.length !== 0 && email.length !== 0) {
    passwordIsAllowed = email !== password;
  } else {
    passwordIsAllowed = true;
  }

  if (passwordIsAllowed) {
    if (password.length > 50) {
      validation.error = true;
      validation.errorMessage = i18n.t('errors:custom.pass_max_length');
      validation.errorCode = options?.submit ? '' : 'length';
    } else if (validation.score < 2) {
      validation.error = true;
      validation.errorCode = 'password_weak';
      validation.errorMessage = i18n.t('auth:increasePasswordStrength');
      if (allPwdCriteriaSatisfied(password)) {
        validation.error = false;
        validation.errorCode = '';
        validation.errorMessage = '';
        validation.score = 2;
      }
    }
  } else {
    validation.error = true;
    validation.errorMessage = i18n.t('errors:custom.pass_email_diff');
    validation.errorCode = options?.submit ? '' : 'email_pass_same';
  }

  return validation;
};

export const checkPasswordConditions = (password = '') => {
  const status = {
    minCharacters: false,
    uppercaseLetter: false,
    lowercaseLetter: false,
    specialCharacter: false,
    number: false,
  };

  if (password.length >= 6) status.minCharacters = true;
  if (ONE_UPPERCASE_LETTER_PATTERN.test(password)) status.uppercaseLetter = true;
  if (ONE_LOWERCASE_LETTER_PATTERN.test(password)) status.lowercaseLetter = true;
  if (ONE_SPECIAL_CHARACTER_PATTERN.test(password)) status.specialCharacter = true;
  if (ONE_NUMBER_PATTERN.test(password)) status.number = true;

  return status;
};

export const getCountryValidation = (country, options) => {
  const validation = {
    error: false,
    errorMessage: null,
    isNotServed: false,
    errorCode: '',
  };

  // Validation on Submit Press.
  if (options?.submit) {
    if (isEmpty(country)) {
      validation.error = true;
      validation.errorMessage = i18n.t('errors:custom.country_empty');
      return validation;
    }
    if (options.errorCode) {
      switch (options.errorCode) {
        case 'invalid_country': {
          validation.error = true;
          validation.errorMessage = i18n.t('errors:user.invalid_country');
          break;
        }
        default:
          break;
      }
      validation.errorCode = options.errorCode;
    }
    return validation;
  }

  // Live Validation.
  validation.isNotServed = contains({ value: country }, countryNotValid) && !isDemo();
  if (validation.isNotServed) {
    validation.error = true;
    validation.errorMessage = <CountryNotServedMessage />;
  }

  return validation;
};

export const getEmailValidation = (email, options) => {
  const validation = {
    error: false,
    errorMessage: '',
    errorCode: '',
  };

  const emailIsEmpty = isEmpty(email);
  const emailFormatIsInValid = !validateEmailPattern(email) && !isEmpty(email);

  // Validation on Submit Press.
  if (options?.submit) {
    if (emailIsEmpty) {
      validation.error = true;
      validation.errorMessage = i18n.t('errors:custom.email_empty');
    } else if (emailFormatIsInValid) {
      validation.error = true;
      validation.errorMessage = i18n.t('errors:custom.emailAddressInvalid');
    } else if (options.errorCode) {
      switch (options.errorCode) {
        case 'already_registered': {
          validation.error = true;
          validation.errorMessage = <AlreadyRegisteredMessage />;
          break;
        }
        case ERROR_CODES.EMAIL_ALREADY_REGISTERED: {
          validation.error = true;
          validation.errorMessage = i18n.t(
            'errors:user.email_already_registered_manually'
          );
          break;
        }
        case 'already_registered_verification_pending': {
          validation.error = true;
          validation.errorMessage = <AlreadyRegisteredUnverifiedMessage email={email} />;
          break;
        }
        case 'invalid_email_format': {
          validation.error = true;
          validation.errorMessage = i18n.t('errors:user.invalid_email_format');
          break;
        }
        case 'blocked_domain': {
          validation.error = true;
          validation.errorMessage = i18n.t('errors:user.blocked_domain');
          break;
        }
        case ERROR_CODES.DELETED_ACCOUNT_EXISTS: {
          validation.error = true;
          validation.errorMessage = (
            <DeletedAccountExistsMessage
              email={email}
              userId={options?.errorContext?.user_id}
            />
          );
          break;
        }
        default:
          break;
      }
      validation.errorCode = options.errorCode;
    }
    return validation;
  }

  // Live Validation.
  if (emailFormatIsInValid) {
    validation.error = true;
  }

  return validation;
};

export const getDefaultError = options => {
  let defaultErrorMessage = '';
  if (options.errorCode) {
    switch (options.errorCode) {
      case 'mandatory_fields_missing':
        defaultErrorMessage = i18n.t('errors:custom.accept_mandatory_fields');
        break;

      case 'bad_schema':
        defaultErrorMessage = options.schemaErrorMessage
          ? options.schemaErrorMessage
          : '';
        break;
      case 'api_blocked':
        defaultErrorMessage = i18n.t('auth:processBlocked');
        break;

      default:
        break;
    }
  }
  return defaultErrorMessage;
};

export const setFieldFocus = ref => {
  ref?.current?.focus();
};

export const getStoredUtmParams = () => {
  const utmParams = JSON.parse(localStorage.getItem(STORAGE_KEYS.UTM_PARAMETERS) || '{}');

  const { utm_source: utmSource, utm_medium: utmMedium } = utmParams;
  return {
    utmMedium,
    utmSource,
  };
};

export const signupHandler = params => {
  const {
    state,
    dispatch,
    appDispatch,
    recaptchaRef,
    history,
    validator,
    location,
    isUserConsentGiven,
  } = params;

  const { utmMedium, utmSource } = getStoredUtmParams();

  const email = state.email.value;
  const password = state.password.value;
  const country = state.country.value;
  const enteredRefCode = state.referral.value;
  // If referral code is empty or invalid then use the default one.
  const referralCode =
    enteredRefCode.length === 0 || state.referral.error
      ? DEFAULT_REFERRAL_CODE
      : enteredRefCode;

  appDispatch(
    signupClicked({
      email,
      country,
    })
  );

  recaptchaRef.current
    .executeAsync()
    .then(token => {
      // body for fetch request.
      const bodyData = {
        email,
        password,
        country,
        signup_referral_code: referralCode,
        recaptcha_response: token,
        device_os_type: getDeviceOSTypeForAnalytics(),
        tracking_info: {},
        user_consent: isUserConsentGiven,
      };

      // Ref for 20 secs timer.
      const apiBlockedTimer = createRef(null);

      // try/catch block
      try {
        mixpanelStartRecording();

        if (typeof token !== 'string') {
          trackMixpanelEvent(MIXPANEL_EVENT_NAMES.CAPTCHA_ERROR, {
            Country: country,
            $email: email,
            referral_code: referralCode,
            recaptcha_response: token,
            page_name: 'Sign-up',
            type: 'email',
            source: 'signup',
          });

          dispatch(actions.recaptchaError());
          recaptchaRef.current?.reset();
          return;
        }

        const deviceId = getDeviceId();

        const request = new Request(`${API_URL}${USER_PROFILE.REGISTER_SIGNUP}`, {
          method: 'POST',
          body: JSON.stringify(bodyData),
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'X-App-Source': 'web',
            'X-App-Version': RELEASE_VERSION || 'unknown',
            'X-Device-Id': deviceId,
          },
        });

        // hide loader, send event to sentry and mixpanel after 10 secs
        // this will get triggered if fetch call didnt work
        apiBlockedTimer.current = setTimeout(() => {
          dispatch(actions.apiBlocked());
          const commonTrackingData = {
            email,
            country,
            page_name: 'Sign-up',
            type: 'email',
          };
          trackMixpanelEvent('Signup API Timeout', commonTrackingData);
          addLogsToSentry(
            { ...omit(['password'], bodyData), ...commonTrackingData },
            'Signup API Timeout'
          );
        }, 20000);

        fetch(request)
          .then(res => res.json())
          .then(res => {
            if (res.success) {
              const { id, pre_2fa_token: pre2faToken } = res.result;

              appDispatch(
                userSignupSuccess({
                  email,
                  pre_2fa_token: pre2faToken,
                })
              );
              dispatch(actions.registerSuccess());
              identifyUser(id);
              setUserProperty({
                registration_time: new Date().toString(),
                Country: country,
                $email: email,
                referral_code: referralCode,
              });
              trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.SUCCESS, {
                registration_time: new Date().toString(),
                Country: country,
                $email: email,
                referral_code: referralCode,
                page_name: 'Sign-up',
                type: 'email',
              });

              postMessageToMobileApp('SIGNUP_NOTIFICATION_SUCCESS', {});

              redirectToEmailVerification(email, history, location.state);
            } else if (res.error) {
              appDispatch(userSignupFail(res.error));
              recaptchaRef.current?.reset();

              const errorObj = res.error;
              const { code, context = {} } = errorObj;
              const errorMsg = context.schema_errors ? context.schema_errors[0] : {};

              trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.FAIL, {
                page_name: 'Sign-up',
                registration_time: new Date().toString(),
                Country: country,
                $email: email,
                error_code: code,
                error_message: errorMsg.message || '',
                type: 'email',
              });

              dispatch(
                actions.registerFailure(code, errorMsg.message || '', validator, context)
              );

              if (!SIGNUP_ERROR_CODES.includes(code)) {
                addLogsToSentry(
                  {
                    email,
                    requestPayload: omit(['password'], bodyData),
                    error: JSON.stringify(errorObj),
                    page_name: 'Sign-up',
                    type: 'email',
                  },
                  `Signup Unhandled Error - ${code}`
                );
              }
            }
          })
          .catch(error => {
            recaptchaRef.current?.reset();
            withScope(scope => {
              scope.setUser({
                email,
                error:
                  error?.message === 'Failed to fetch'
                    ? 'Failed to fetch'
                    : JSON.stringify(error),
                requestPayload: omit(['password'], bodyData),
              });
              scope.setExtras(error);
              captureMessage('Signup Unhandled Error');
              captureException(error);
            });

            dispatch(actions.setDefaultError(i18n.t('errors:user.defaultMessage')));
            dispatch(actions.setLoader(false));

            trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.API_CATCH_BLOCK, {
              $email: email,
              error: error.toString(),
              type: 'email',
            });
          })
          .finally(() => {
            clearTimeout(apiBlockedTimer.current);
          });
      } catch (error) {
        withScope(scope => {
          scope.setUser({
            email,
            error:
              error?.message === 'Failed to fetch'
                ? 'Failed to fetch'
                : JSON.stringify(error),
          });
          scope.setExtras(error);
          captureMessage('Signup Unhandled Error');
          captureException(error);
        });

        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.DEFAULT_CATCH_BLOCK, {
          $email: email,
          error: error.toString(),
          type: 'email',
        });
      }
    })
    .catch(() => {
      trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.CAPTCHA_ERROR, {
        Country: country,
        $email: email,
        referral_code: referralCode,
        page_name: 'Sign-up',
        type: 'email',
        source: 'signup',
      });
      dispatch(actions.recaptchaError());
    });

  trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.BUTTON_CLICKED, {
    Country: country,
    $email: email,
    referral_code: referralCode,
    page_name: 'Sign-up',
    type: 'email',
    ...(utmSource && { utm_source: utmSource }),
    ...(utmMedium && { utm_source: utmSource }),
  });
};

/**
 * @typedef {Object} OAuthHandlerParams
 * @property {{ success: boolean, error?: any, result?: any }} res - API response data.
 * @property {Object} bodyData - Request payload sent to the API.
 * @property {string} country - User’s country.
 * @property {string} [referralCode] - Optional referral code.
 * @property {string} pageName - Page context (e.g., "Login", "Sign-up").
 * @property {string} loginType - Type of login ("oauth", "one_tap", etc.).
 */

/**
 * Handles OAuth signup response.
 * @param {OAuthHandlerParams} params
 */
const signUpSuccessHandler = params => {
  const { res, country, referralCode, pageName, loginType } = params;
  const result = res?.result;
  identifyUser(result.id);

  if (!result?.new_registration) {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_IN.SUCCESS, {
      page_name: pageName,
      is_loggedin: true,
      type: loginType,
      $email: result.email,
    });

    postMessageToMobileApp('LOGIN_NOTIFICATION_SUCCESS', {});
  } else {
    setUserProperty({
      registration_date: new Date().toString(),
      Country: country,
      $email: result.email,
      type: loginType,
    });

    postMessageToMobileApp('SIGNUP_NOTIFICATION_SUCCESS', {});

    removeTrackierClickId();

    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.SUCCESS, {
      registration_time: new Date().toString(),
      Country: country,
      referral_code: referralCode,
      page_name: pageName,
      type: loginType,
      $email: result.email,
    });
    sendTrackierSignupEvent(result.id);

    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.EMAIL_VERIFICATION_COMPLETED, {
      email_verification_time: new Date().toString(),
      page_name: pageName,
      type: loginType,
      is_sub_account: result.is_sub_account,
      $email: result.email,
    });

    dispatchGtmSignupEvent(result.id, referralCode, result.email);

    if (
      !IS_HYBRID_APP_SUPPORTED &&
      checkRefCodeValidForSendingAttributionData(referralCode)
    ) {
      postMessageToMobileApp('APPSFLYER_SIGNUP', {
        user_id: result.id,
        country,
        referralcode: referralCode,
      });

      if (result?.token) {
        postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.SEND_APPS_FLYER_ID, {
          token: result.token,
        });
      }
    }

    MoengageLib.signUp({
      id: result.id,
      country: result.country,
      pageName,
      email: result.email,
      isMobileApp: isRNWebview(),
    });
  }
};

export const oAuthSignup = ({
  bodyData,
  userEmail,
  history,
  dispatch,
  loginDispatch,
  appDispatch,
  setOAuthLoginState,
  provider,
  redirectURI,
  country,
  referralCode,
  loginPageIndianExchange,
  isOneTapLogin,
}) => {
  const body = { ...bodyData };
  if (provider === OAUTH_PROVIDERS.APPLE) {
    body.redirect_uri = redirectURI;
  }
  let pageName;
  if (loginPageIndianExchange) {
    pageName = 'Login';
  } else if (isOneTapLogin) {
    pageName = getMixpanelPageName();
  } else {
    pageName = 'Sign-up';
  }

  const loginType = isOneTapLogin ? 'one_tap' : provider;
  const deviceId = getDeviceId();

  const request = new Request(OAUTH.REGISTER, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      'X-App-Source': 'web-app',
      'X-App-Version': RELEASE_VERSION || 'unknown',
      'X-Device-Id': deviceId,
    },
  });

  fetchWithTimeout(request, () => {
    requestAnimationFrame(() => {
      addLogsInSentryAndMixpanel({}, MIXPANEL_EVENT_NAMES.API_TIMEOUT_TRACKING, {
        extra: {
          API: OAUTH.REGISTER,
          source: window?.location?.pathname,
          site_demarkation: 'Web App',
          isReactNative: isRNWebview(),
          payload: omit(['oauth_token'], body),
          timeStamp: new Date().toISOString(),
          method: 'POST',
          bandwidth: window?.navigator?.connection?.downlink,
        },
      });
    });
  })
    .then(res => res.json())
    .then(res => {
      const { error, success, result } = res;
      if (!success) {
        const { code: errorCode, jwt_token: jwtToken, context: errorContext } = error;

        const payload = {
          error: true,
          errorMessage: i18n.t(`errors:user.${errorCode}`),
          errorCode,
          errorContext,
        };

        if (errorCode === ERROR_CODES.LOGIN_BLOCKED) {
          payload.errorMessage = i18n.t(`errors:custom.${errorCode}`);
        }

        if (errorCode === ERROR_CODES.PENDING_USER_CONSENT) {
          appDispatch(
            setConsentPending({
              preAuthToken: error?.context?.pre_2fa_token,
              provider,
              source:
                (isOneTapLogin && 'one_tap') ||
                (loginPageIndianExchange && 'login') ||
                'signup',
            })
          );
          trackMixpanelEvent(MIXPANEL_EVENT_NAMES.CONSENT.OAUTH_DONE_CONSENT_PENDING, {
            type: isOneTapLogin ? 'one_tap' : provider,
          });
        }

        if (errorCode === ERROR_CODES.OAUTH_NOT_ACTIVE) {
          localStorage.setItem(STORAGE_KEYS.LINK_OAUTH_EMAIL, JSON.stringify(userEmail));
          localStorage.setItem(
            STORAGE_KEYS.LINK_OAUTH_JWT_TOKEN,
            JSON.stringify(jwtToken)
          );
          localStorage.setItem(
            STORAGE_KEYS.LINK_OAUTH_PROVIDER,
            JSON.stringify(provider)
          );
          history.push(routes.linkAccount);
        }

        if (errorCode === ERROR_CODES.DELETED_ACCOUNT_EXISTS) {
          payload.errorMessage = errorCode;
        }

        if (errorCode !== ERROR_CODES.PENDING_USER_CONSENT) {
          trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SIGN_UP.FAIL, {
            page_name: 'Sign-up',
            registration_time: new Date().toString(),
            Country: country,
            $email: userEmail,
            error_code: errorCode,
            error_message: i18n.t(`errors:user.${errorCode}`),
            type: loginType,
          });
          if (loginPageIndianExchange || isOneTapLogin) {
            loginDispatch(loginActions.oAuthLoginError(payload));
          } else {
            dispatch(actions.oAuthSignUpError(payload));
          }
        }

        if (
          ![
            ERROR_CODES.LOGIN_BLOCKED,
            ERROR_CODES.OAUTH_NOT_ACTIVE,
            ERROR_CODES.DELETED_ACCOUNT_EXISTS,
            ERROR_CODES.USER_NOT_FOUND,
            ERROR_CODES.EMAIL_ALREADY_REGISTERED,
            ERROR_CODES.PENDING_USER_CONSENT,
          ].includes(errorCode)
        ) {
          addLogsToSentry(
            { requestPayload: body, error: JSON.stringify(error), email: userEmail },
            'OAuth Signup Unhandled Error'
          );
        }
      } else if (
        result.pre_2fa_token &&
        (result.code === 'mfa_code' || result.code === 'phone_otp')
      ) {
        if (loginPageIndianExchange || isOneTapLogin) {
          loginDispatch(
            loginActions.login_Pre2FA_Success({
              pre2faToken: result.pre_2fa_token,
              code: result.code,
              email: userEmail,
              phoneNumber: result.phone_number,
              phoneNumberAdded: result.phone_number_added,
              phoneOTPRequired: result.phone_otp_required,
            })
          );
        } else {
          dispatch(
            actions.signupPre2FASuccess({
              pre2faToken: result.pre_2fa_token,
              code: result.code,
              email: userEmail,
              phoneNumber: result.phone_number,
              phoneNumberAdded: result.phone_number_added,
              phoneOTPRequired: result.phone_otp_required,
            })
          );
        }
      } else {
        signUpSuccessHandler({
          res,
          appDispatch,
          country,
          referralCode,
          pageName,
          loginType,
        });
        appDispatch(oAuthLoginSuccess(res));
      }
    })
    .catch(error => {
      const requestPayload = omit(['oauth_token'], body);

      withScope(scope => {
        scope.setUser({
          email: userEmail,
          type: loginType,
          error:
            error?.message === 'Failed to fetch'
              ? 'Failed to fetch'
              : JSON.stringify(error),
          requestPayload,
        });
        scope.setExtras(error);
        captureMessage('OAuth Signup Unhandled Error');
        captureException(error);
      });
      if (loginPageIndianExchange || isOneTapLogin) {
        loginDispatch(
          loginActions.oAuthLoginError({
            error: true,
            errorMessage: i18n.t(`errors:defaultMessage`),
            errorCode: 'defaultMessage',
          })
        );
      } else {
        dispatch(
          actions.oAuthSignUpError({
            error: true,
            errorMessage: i18n.t(`errors:defaultMessage`),
            errorCode: 'defaultMessage',
          })
        );
      }
    })
    .finally(() => {
      if (isRNWebview()) {
        postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.OAUTH_LOGOUT, {
          provider: OAUTH_PROVIDERS.GOOGLE,
        });
        setOAuthLoginState();
      }
      // dispatch will be undefined in case of login page indian exchange or one tap login
      if (loginPageIndianExchange || isOneTapLogin) {
        loginDispatch(loginActions.setOAuthBtnLoader(false));
      } else {
        dispatch(actions.setOAuthBtnLoader(false));
      }
    });
};

export const oAuthSignupHandler = params => {
  mixpanelStartRecording();

  const {
    state,
    dispatch,
    loginDispatch,
    appDispatch,
    accessToken,
    setOAuthLoginState,
    history,
    provider,
    redirectURI,
    idToken,
    isUserConsentGiven,
    email,
  } = params;

  let country;
  const path = window.location.pathname;
  const loginPageIndianExchange = path.includes(routes.login) && IS_INDIAN_EXCHANGE;
  let pageName;
  if (loginPageIndianExchange) {
    country = 'India';
    pageName = 'Login';
  } else {
    country = state.country.value;
    pageName = 'Sign-up';
  }

  const enteredRefCode = state?.referral.value;
  // If referral code is empty or invalid then use the default one.
  const referralCode =
    enteredRefCode?.length === 0 || state?.referral.error
      ? DEFAULT_REFERRAL_CODE
      : enteredRefCode;

  const trackierClickId = getTrackierClickId(referralCode);

  if (loginPageIndianExchange) {
    loginDispatch(loginActions.setOAuthBtnLoader(true));
  } else {
    dispatch(actions.setOAuthBtnLoader(true));
  }

  const bodyData = {
    oauth_token: accessToken,
    country,
    signup_referral_code: referralCode,
    tracking_info: {},
    oauth: provider,
    device_os_type: getDeviceOSTypeForAnalytics(),
    trackier_click_id: trackierClickId,
    user_consent: isUserConsentGiven,
  };

  if (provider === OAUTH_PROVIDERS.GOOGLE) {
    if (isRNWebview() && IS_RN_WEBVIEW_ANDROID && email) {
      oAuthSignup({
        bodyData,
        userEmail: email,
        history,
        dispatch,
        loginDispatch,
        appDispatch,
        setOAuthLoginState,
        provider,
        country,
        referralCode,
        loginPageIndianExchange,
      });
    } else {
      fetch(`${OAUTH.GOOGLE_USER_INFO}${accessToken}`)
        .then(response => response.json())
        .then(response => {
          const userEmail = response.email;
          oAuthSignup({
            bodyData,
            userEmail,
            history,
            dispatch,
            loginDispatch,
            appDispatch,
            setOAuthLoginState,
            provider,
            country,
            referralCode,
            loginPageIndianExchange,
          });
        })
        .catch(() => {
          setOAuthLoginState();
          addLogsInSentryAndMixpanel(
            {
              referral_code: referralCode,
            },
            MIXPANEL_EVENT_NAMES.SIGN_UP.GOOGLE_ACCESS_TOKEN_VALIDATION_FAILED,
            {
              extra: {
                Country: country,
                referral_code: referralCode,
                page_name: pageName,
                type: state.oAuth.provider,
              },
            }
          );
          dispatch(
            actions.oAuthSignUpError({
              error: true,
              errorMessage: i18n.t(`errors:defaultMessage`),
              errorCode: 'defaultMessage',
            })
          );
        });
    }
  } else if (provider === OAUTH_PROVIDERS.APPLE) {
    const decoded = jwtDecode(idToken);
    const userEmail = decoded.email;
    oAuthSignup({
      bodyData,
      userEmail,
      history,
      dispatch,
      loginDispatch,
      appDispatch,
      setOAuthLoginState,
      provider,
      redirectURI,
      country,
      referralCode,
      loginPageIndianExchange,
    });
  } else {
    dispatch(
      actions.oAuthSignUpError({
        error: true,
        errorMessage: i18n.t(`errors:defaultMessage`),
        errorCode: 'defaultMessage',
      })
    );
  }
};

export const consentLoginHandler = async params => {
  const {
    bodyData,
    dispatch,
    provider,
    referralCode = DEFAULT_REFERRAL_CODE,
    loginPageIndianExchange,
    isOneTapLogin,
  } = params;

  const body = { ...bodyData };
  let pageName;
  if (loginPageIndianExchange) {
    pageName = 'Login';
  } else if (isOneTapLogin) {
    pageName = getMixpanelPageName();
  } else {
    pageName = 'Sign-up';
  }

  const loginType = isOneTapLogin ? 'one_tap' : provider;

  body.trackier_click_id = getTrackierClickId(referralCode);
  body.device_os_type = getDeviceOSTypeForAnalytics();

  return dispatch(loginWithConsent(body)).then(res => {
    if (res.result) {
      // eslint-disable-next-line no-param-reassign
      res.result.new_registration = true;
    }
    signUpSuccessHandler({
      res,
      country: res?.result?.country,
      referralCode,
      pageName,
      loginType,
    });
  });
};
